import React, { FunctionComponent } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import Radial from "../../Charts/Radial";
import { ChartTitle } from "../styles/employer-analytics-styles";
import {
  Wrapper,
  Charts,
  Chart,
  ChartName,
} from "../styles/placement-summary-styles";

export interface IPlacementSummaryProps {
  totalJobsPosted: number;
  filledByPsl: number;
  filledByMarketplace: number;
  pdfExport?: boolean;
}

export const PlacementSummary: FunctionComponent<IPlacementSummaryProps> = ({
  totalJobsPosted,
  filledByPsl,
  filledByMarketplace,
  pdfExport,
}) => {
  const totalJobsPostedOptions = {
    series: [totalJobsPosted],
    options: {
      height: 170,
      chart: {
        type: "radialBar",
        toolbar: {
          show: false,
        },
      },
      plotOptions: {
        radialBar: {
          startAngle: -90,
          endAngle: 90,
          hollow: {
            margin: 0,
            size: "70%",
            background: "#fff",
            image: undefined,
            imageOffsetX: 0,
            imageOffsetY: 0,
            position: "front",
          },
          track: {
            background: "#fff",
            strokeWidth: "95%",
            margin: 0,
            dropShadow: {
              enabled: true,
              top: -2,
              left: 0,
              blur: 4,
              opacity: 0.2,
            },
          },
          dataLabels: {
            show: true,
            name: {
              offsetY: 0,
              show: true,
              color: "#888",
              fontSize: "15px",
            },
            value: {
              formatter: (val: number) => val,
              color: "#111",
              offsetY: -35,
              fontSize: "24px",
              show: true,
            },
          },
        },
      },
      colors: ["rgb(0, 143, 251)"],
      labels: ["jobs"],
    },
  };

  const filledByPslOptions = {
    series: [filledByPsl],
    options: {
      height: 170,
      chart: {
        type: "radialBar",
        toolbar: {
          show: false,
        },
      },
      plotOptions: {
        radialBar: {
          startAngle: -90,
          endAngle: 90,
          hollow: {
            margin: 0,
            size: "70%",
            background: "#fff",
            image: undefined,
            imageOffsetX: 0,
            imageOffsetY: 0,
            position: "front",
          },
          track: {
            background: "#fff",
            strokeWidth: "95%",
            margin: 0,
            dropShadow: {
              enabled: true,
              top: -2,
              left: 0,
              blur: 4,
              opacity: 0.2,
            },
          },
          dataLabels: {
            show: true,
            name: {
              offsetY: 0,
              show: true,
              color: "#888",
              fontSize: "15px",
            },
            value: {
              formatter: (val: number) => val,
              color: "#111",
              offsetY: -35,
              fontSize: "24px",
              show: true,
            },
          },
        },
      },
      colors: ["#79D9D9"],
      labels: ["jobs"],
    },
  };

  const filledByMarketplaceOptions = {
    series: [filledByMarketplace],
    options: {
      height: 170,
      chart: {
        type: "radialBar",
        toolbar: {
          show: false,
        },
      },
      plotOptions: {
        radialBar: {
          startAngle: -90,
          endAngle: 90,
          hollow: {
            margin: 0,
            size: "70%",
            background: "#fff",
            image: undefined,
            imageOffsetX: 0,
            imageOffsetY: 0,
            position: "front",
          },
          track: {
            background: "#fff",
            strokeWidth: "95%",
            margin: 0,
            dropShadow: {
              enabled: true,
              top: -2,
              left: 0,
              blur: 4,
              opacity: 0.2,
            },
          },
          dataLabels: {
            show: true,
            name: {
              offsetY: 0,
              show: true,
              color: "#888",
              fontSize: "15px",
            },
            value: {
              formatter: (val: number) => val,
              color: "#111",
              offsetY: -35,
              fontSize: "24px",
              show: true,
            },
          },
        },
      },
      colors: ["rgb(0, 227, 150)"],
      labels: ["jobs"],
    },
  };

  return (
    <Wrapper className="animated fadeIn" pdfExport={pdfExport}>
      <ChartTitle
        marginBottom={5}
        data-test-id="placement-summary-charts-title"
      >
        Placement Summary
      </ChartTitle>
      <Charts data-test-id="placement-summary-charts" pdfExport={pdfExport}>
        <Chart className="animated fadeIn delay-1">
          <Radial
            graphOptions={totalJobsPostedOptions}
            dataTestId="total-jobs-posted"
          />
          <ChartName>Total jobs posted</ChartName>
        </Chart>
        <Chart className="animated fadeIn delay-1">
          <Radial
            graphOptions={filledByPslOptions}
            dataTestId="filled-by-psl"
          />
          <ChartName>Filled by PSL</ChartName>
        </Chart>
        <Chart className="animated fadeIn delay-1">
          <Radial
            graphOptions={filledByMarketplaceOptions}
            dataTestId="filled-by-marketplace"
          />
          <ChartName>Filled by Marketplace</ChartName>
        </Chart>
      </Charts>
    </Wrapper>
  );
};

PlacementSummary.propTypes = {
  totalJobsPosted: PropTypes.number,
  filledByPsl: PropTypes.number,
  filledByMarketplace: PropTypes.number,
  pdfExport: PropTypes.bool,
};

PlacementSummary.defaultProps = {
  totalJobsPosted: 0,
  filledByPsl: 0,
  filledByMarketplace: 0,
  pdfExport: false,
};

interface IReduxState {
  employerAnalytics: {
    agencyPerformanceData: {
      placementSummary: {
        totalJobsPosted: number;
        filledByPsl: number;
        filledByMarketplace: number;
      };
    };
  };
}

const mapStateToProps = (state: IReduxState) => ({
  totalJobsPosted:
    state.employerAnalytics.agencyPerformanceData.placementSummary
      .totalJobsPosted,
  filledByPsl:
    state.employerAnalytics.agencyPerformanceData.placementSummary.filledByPsl,
  filledByMarketplace:
    state.employerAnalytics.agencyPerformanceData.placementSummary
      .filledByMarketplace,
});

export default connect(mapStateToProps, {})(PlacementSummary);
